// What each homeland brings: its zones (town districts, fields, its continent's overworld), the places on that
// overworld, its doors and zone lines, its mentors and battle trainer, where new characters start, its ferry
// pier, its recoloured creatures, its quests and forage, and its intro film. Sunvale's content is the original
// game (spread around shared/zones.ts and friends); the other two live in shared/nation-<id>.ts.
import type { ZoneDef, Place } from './zones.js';
import type { Door, Interior } from './interiors.js';
import type { Mentor } from './mentors.js';
import type { MobDef } from './mobs.js';
import type { QuestDef, Area } from './quests.js';
import type { ForageNode } from './world.js';
import type { NationId } from './nations.js';
import { THORNWOOD } from './nation-thornwood.js';
import { MOONMERE } from './nation-moonmere.js';

/** One camera move of an intro film (tiles; see client/src/ui/cinema.ts). */
export interface IntroShot { zone: string; from: [number, number]; to: [number, number]; zoom: [number, number]; ms: number; caption?: string; hour?: number }
export interface NationContent {
  id: NationId;
  zones: ZoneDef[];
  /** Places on its overworld (Place.world = that overworld's zone id). */
  places: Place[];
  doors: Door[];
  /** Its five mentors (one per job) and its battle trainer. */
  mentors: Mentor[];
  trainer: { name: string; map: string; at: [number, number] };
  /** A new character steps out of their home here (and the home's door leads back). */
  home: { zone: string; door: [number, number]; at: [number, number] };
  /** The ferry pier (a door on the town's water), and where you step off when you arrive. */
  dock: { zone: string; tile: [number, number]; at: [number, number]; name: string };
  mobs: Record<string, MobDef>;
  /** Encounter pools for wandering shadows on its overworld, and which arena each of its zones fights in. */
  encounters?: Record<string, string[]>;
  arenas?: Record<string, 'meadow' | 'woods' | 'grave' | 'spine' | 'crags'>;
  quests?: QuestDef[]; areas?: Area[]; forage?: ForageNode[];
  /** Rooms (shops, an inn, a hall): add their doors in `doors` too. Opening hours go in `shopHours`. */
  rooms?: Interior[]; shopHours?: Record<string, [number, number]>;
  /** The film a new character from here sees before waking up: the continent, then the town, then home. */
  intro: IntroShot[];
}

export const SUNVALE: NationContent = {
  id: 'sunvale', zones: [], places: [], doors: [], mentors: [], trainer: { name: 'Drillmaster Pell', map: 'town', at: [32, 18] },
  home: { zone: 'cottages', door: [6, 7], at: [6, 8] },
  dock: { zone: 'harbor', tile: [20, 20], at: [20, 18], name: 'Tiny Town Harbor' },
  mobs: {},
  intro: [
    { zone: 'world', from: [30, 20], to: [40, 27], zoom: [0.7, 0.9], ms: 5200, hour: 5.4, caption: 'The Sunvale Isles: golden fields, a river that bends, and a mountain where something sleeps.' },
    { zone: 'town', from: [31, 22], to: [19.5, 15], zoom: [0.9, 1.25], ms: 5600, hour: 6.1, caption: 'In Tiny Town the bakers light their ovens. The fountain wakes up. Somebody is always humming.' },
    { zone: 'cottages', from: [6, 11], to: [24, 9], zoom: [1.1, 1.5], ms: 5600, hour: 6.6, caption: 'And down on Cottage Lane, behind a green door…' },
  ],
};
export const HOMELANDS: Record<NationId, NationContent> = { sunvale: SUNVALE, thornwood: THORNWOOD, moonmere: MOONMERE };
export const OTHER_HOMELANDS: NationContent[] = [THORNWOOD, MOONMERE];
export const homeland = (id: string | undefined): NationContent => HOMELANDS[id as NationId] ?? SUNVALE;
